/**
 * LocalStorage-based offline queue for corruption reports.
 *
 * Implements IOfflineStorage for browser environments.
 * Reports are persisted to survive page reloads and sent when back online.
 */

import { DebugLogger } from '@affine/debug';
import { Service } from '@toeverything/infra';

import type { IOfflineStorage, QueuedReport } from './interfaces';

const logger = new DebugLogger('corruption-reporter:storage');

const STORAGE_KEY = 'affine:corruption-reports-queue';
const MAX_QUEUE_SIZE = 50;

export class LocalStorageOfflineQueue
  extends Service
  implements IOfflineStorage
{
  getQueue(): QueuedReport[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      logger.warn('Failed to read offline queue', { error });
      return [];
    }
  }

  saveQueue(queue: QueuedReport[]): void {
    try {
      // Enforce max size
      const trimmedQueue =
        queue.length > MAX_QUEUE_SIZE ? queue.slice(-MAX_QUEUE_SIZE) : queue;

      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedQueue));
    } catch (error) {
      logger.error('Failed to save offline queue', { error });
    }
  }

  clearQueue(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      logger.warn('Failed to clear offline queue', { error });
    }
  }
}
